import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useRef } from 'react'

import { useCustomT } from '@flowwai-frontend/shared/common-components'

import { theme } from '../../theme'

import { OTPFieldValidationConfig } from '../../types/ValidateFieldWithOTPTypes'
import OTPInputField from '../OTPInput/OTPInput'

import * as Styles from './styles'

interface Props {
   headerText: string
   otpFieldValidationConfig: OTPFieldValidationConfig
   otpValue: string
   timerDurationInSecs: number

   handleResend: () => void
   setOTPValue: (value: string) => void

   containerClassName?: string
   hideSeparator?: boolean
   isResendOTPLoading?: boolean
   maskedEmail?: string
   otpErrorMessage?: string
   otpLabel?: string

   renderHeaderIcon?: () => React.ReactNode
}

const OTPVerification = (props: Props): React.ReactElement => {
   const {
      headerText,
      otpFieldValidationConfig,
      otpValue,
      timerDurationInSecs,

      setOTPValue,
      handleResend,

      containerClassName,
      hideSeparator,
      isResendOTPLoading,
      maskedEmail,
      otpErrorMessage,
      otpLabel,

      renderHeaderIcon
   } = props

   const t = useCustomT('common:otpVerification')

   const commonTheme = theme.common

   const timerRef = useRef<{
      start: () => void
   } | null>(null)

   const labelStyles = cn(Styles.labelTextStyles, commonTheme.defaultLabelColor)
   const valueStyles = cn(
      Styles.otpValueStyles,
      commonTheme.defaultDescriptionColor
   )

   const handleResendWithTimer = (): void => {
      timerRef.current?.start()
      handleResend()
   }

   const renderEmail = (): React.ReactElement => (
      <div>
         <span className={labelStyles}>{t('email')}</span>{' '}
         <span className={valueStyles}>{maskedEmail}</span>
      </div>
   )

   const renderHeaderText = (): React.ReactElement => (
      <h2
         className={cn(
            Styles.headerTextStyles,
            commonTheme.defaultHeadingColor
         )}
      >
         {headerText}
      </h2>
   )

   const renderHeader = (): React.ReactElement => (
      <div className={cn(Styles.headerStyles)}>
         {renderHeaderIcon?.() ?? null}
         <div className={cn(Styles.headerTextContainerStyles)}>
            {renderHeaderText()}
            <div className={cn(Styles.descriptionStyles)}>
               <p className={labelStyles}>
                  {otpFieldValidationConfig.otpDestinationType ===
                  'PHONE_NUMBER'
                     ? t('enterOtpForPhoneNumberFieldValidation', {
                          maskedMobileNumber:
                             otpFieldValidationConfig.otpDestination
                       })
                     : ''}
               </p>
               {maskedEmail ? renderEmail() : null}
            </div>
         </div>
      </div>
   )

   const renderInputField = (): React.ReactElement => (
      <div className={cn(Styles.otpSectionStyles)}>
         <OTPInputField
            otpValue={otpValue}
            setOTPValue={setOTPValue}
            handleResend={handleResendWithTimer}
            errorMessage={otpErrorMessage}
            timerRef={timerRef}
            timeDurationInSecs={timerDurationInSecs}
            isResendOtpLoading={isResendOTPLoading}
            hideSeparator={hideSeparator}
            otpLabel={otpLabel ?? t('verificationCode')}
         />
      </div>
   )

   return (
      <div className={cn(Styles.containerStyles, containerClassName)}>
         {renderHeader()}
         {renderInputField()}
      </div>
   )
}

export default observer(OTPVerification)
